// src/pages/lms/LMSExams.jsx — Enterprise Exam Management
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import LMSLayout from './LMSLayout';
import {
  Plus, X, Trash2, Edit, Eye, Calendar, Clock,
  CheckCircle, AlertCircle, Search, Download,
  ChevronRight, BookOpen, Award, BarChart3, RefreshCw,
} from 'lucide-react';

const TERM_TYPES = [
  { value:'mid_term',      label:'Mid Term'         },
  { value:'final_term',    label:'Final Term'       },
  { value:'pre_board',     label:'Pre Board'        },
  { value:'board',         label:'Board Exam'       },
  { value:'quarterly',     label:'Quarterly'        },
  { value:'half_yearly',   label:'Half Yearly'      },
  { value:'annual',        label:'Annual'           },
  { value:'supplementary', label:'Supplementary'    },
  { value:'test',          label:'Class Test'       },
  { value:'quiz',          label:'Quiz'             },
  { value:'other',         label:'Other'            },
];

const STATUS_STYLES = {
  upcoming:          { bg:'#EFF6FF', color:'#1D4ED8', border:'#BFDBFE', label:'Upcoming'          },
  ongoing:           { bg:'#FFF7ED', color:'#C2410C', border:'#FED7AA', label:'Ongoing'           },
  completed:         { bg:'#F0FDF4', color:'#065F46', border:'#BBF7D0', label:'Completed'         },
  cancelled:         { bg:'#FEF2F2', color:'#991B1B', border:'#FECACA', label:'Cancelled'         },
  result_announced:  { bg:'#F5F3FF', color:'#5B21B6', border:'#DDD6FE', label:'Results Out'      },
};

const GRADE_COLORS = {
  'A+':'#10B981','A':'#10B981','A-':'#34D399',
  'B+':'#3B82F6','B':'#3B82F6','B-':'#60A5FA',
  'C+':'#F59E0B','C':'#F59E0B','C-':'#FBBF24',
  'D+':'#F97316','D':'#F97316',
  'F':'#EF4444','AB':'#94A3B8',
};

export default function LMSExams() {
  const { account, institution } = useLMSAuth();
  const [view,      setView]      = useState('terms'); // terms|datesheet|results|gazette
  const [terms,     setTerms]     = useState([]);
  const [classes,   setClasses]   = useState([]);
  const [teachers,  setTeachers]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selTerm,   setSelTerm]   = useState(null);
  const [datesheets,setDatesheets]= useState([]);
  const [results,   setResults]   = useState([]);
  const [gazette,   setGazette]   = useState(null);
  const [selClass,  setSelClass]  = useState(null);
  const [students,  setStudents]  = useState([]);
  const [showTermForm,   setShowTermForm]   = useState(false);
  const [showDSForm,     setShowDSForm]     = useState(false);
  const [showResultForm, setShowResultForm] = useState(false);
  const [editTerm,  setEditTerm]  = useState(null);
  const [editDS,    setEditDS]    = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [search,    setSearch]    = useState('');
  const [gazetteLoading, setGazetteLoading] = useState(false);

  const emptyTerm = {
    name:'', term_type:'mid_term', academic_year:'', semester:'',
    target_class_id:'', target_degree:'all', target_year:'', target_department:'',
    start_date:'', end_date:'', result_announcement_date:'',
    status:'upcoming', is_published:true, instructions:'',
  };
  const emptyDS = {
    class_id:'', class_name:'', subject:'', subject_code:'',
    teacher_id:'', exam_date:'', start_time:'09:00', end_time:'12:00',
    room:'', building:'', total_marks:100, passing_marks:40,
    exam_type:'written', syllabus_covered:'', instructions:'',
  };
  const [termForm, setTermForm] = useState(emptyTerm);
  const [dsForm,   setDsForm]   = useState(emptyDS);

  // Bulk result entry
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkDS,      setBulkDS]      = useState(null);
  const [bulkSaving,  setBulkSaving]  = useState(false);

  useEffect(() => { if (institution?.id) loadInit(); }, [institution?.id]);

  const loadInit = async () => {
    setLoading(true);
    try {
      const [tRes, cRes, teachRes] = await Promise.all([
        supabase.from('lms_exam_terms').select('*')
          .eq('institution_id', institution.id)
          .order('start_date', { ascending: false }),
        supabase.from('lms_classes').select('*')
          .eq('institution_id', institution.id).eq('is_active', true).order('name'),
        supabase.from('lms_teachers').select('id,full_name,subjects')
          .eq('institution_id', institution.id).eq('status','active').order('full_name'),
      ]);
      setTerms(tRes.data || []);
      setClasses(cRes.data || []);
      setTeachers(teachRes.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadDatesheets = async (term) => {
    setSelTerm(term);
    setView('datesheet');
    try {
      const { data } = await supabase.from('lms_exam_datesheets')
        .select('*').eq('exam_term_id', term.id)
        .order('exam_date').order('start_time');
      setDatesheets(data || []);
    } catch(e) { console.error(e); }
  };

  const loadResults = async (term) => {
    setSelTerm(term);
    setView('results');
    try {
      const { data } = await supabase.from('lms_exam_results')
        .select('*').eq('exam_term_id', term.id)
        .order('class_name').order('student_name').order('subject');
      setResults(data || []);
    } catch(e) { console.error(e); }
  };

  const loadGazette = async (term, cls) => {
    setSelTerm(term); setSelClass(cls);
    setView('gazette'); setGazetteLoading(true);
    try {
      const { data } = await supabase.rpc('get_class_gazette', {
        p_exam_term_id: term.id,
        p_class_id:     cls.id,
      });
      setGazette(data);
    } catch(e) { console.error(e); }
    finally { setGazetteLoading(false); }
  };

  const loadStudentsForBulk = async (ds) => {
    setBulkDS(ds);
    try {
      // Get students for this class
      const cls = classes.find(c => c.id === ds.class_id);
      if (!cls) return;
      let q = supabase.from('profiles').select('id,full_name,username').eq('institution_id', institution.id).eq('role','student').order('full_name');
      const { data: studs } = await q;
      // Get existing results
      const { data: existing } = await supabase.from('lms_exam_results')
        .select('*').eq('exam_term_id', selTerm.id).eq('datesheet_id', ds.id);
      const existingMap = {};
      (existing || []).forEach(r => { existingMap[r.student_id] = r; });
      // Build bulk entry rows
      setBulkResults((studs || []).map(s => ({
        student_id:      s.id,
        student_name:    s.full_name || '',
        student_username:s.username  || '',
        student_roll_number:'',
        marks_obtained:  existingMap[s.id]?.marks_obtained ?? '',
        is_absent:       existingMap[s.id]?.is_absent       ?? false,
        teacher_remarks: existingMap[s.id]?.teacher_remarks ?? '',
      })));
      setShowResultForm(true);
    } catch(e) { console.error(e); }
  };

  // Save term
  const saveTerm = async () => {
    if (!termForm.name.trim()) { setError('Term name required'); return; }
    if (!termForm.start_date || !termForm.end_date) { setError('Start and end date required'); return; }
    setSaving(true); setError('');
    try {
      const cls = classes.find(c => c.id === termForm.target_class_id);
      const data = {
        ...termForm,
        institution_id:         institution.id,
        institution_account_id: account.id,
        target_class_name:      cls?.name || '',
        target_year:            termForm.target_year ? Number(termForm.target_year) : null,
        target_class_id:        termForm.target_class_id || null,
        result_announcement_date: termForm.result_announcement_date || null,
      };
      if (editTerm) {
        await supabase.from('lms_exam_terms').update(data).eq('id', editTerm.id);
      } else {
        await supabase.from('lms_exam_terms').insert(data);
      }
      setSuccess(editTerm ? 'Exam term updated!' : 'Exam term created!');
      await loadInit();
      setShowTermForm(false); setEditTerm(null); setTermForm(emptyTerm);
      setTimeout(() => setSuccess(''), 3000);
    } catch(e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  // Save datesheet
  const saveDS = async () => {
    if (!dsForm.class_id) { setError('Class required'); return; }
    if (!dsForm.subject.trim()) { setError('Subject required'); return; }
    if (!dsForm.exam_date) { setError('Exam date required'); return; }
    setSaving(true); setError('');
    try {
      const cls = classes.find(c => c.id === dsForm.class_id);
      const t   = teachers.find(t => t.id === dsForm.teacher_id);
      const data = {
        ...dsForm,
        institution_id:         institution.id,
        institution_account_id: account.id,
        exam_term_id:           selTerm.id,
        class_name:             cls?.name || '',
        teacher_name:           t?.full_name || '',
        teacher_id:             dsForm.teacher_id || null,
        total_marks:            Number(dsForm.total_marks) || 100,
        passing_marks:          Number(dsForm.passing_marks) || 40,
      };
      if (editDS) {
        await supabase.from('lms_exam_datesheets').update(data).eq('id', editDS.id);
      } else {
        await supabase.from('lms_exam_datesheets').insert(data);
      }
      setSuccess('Datesheet entry saved!');
      await loadDatesheets(selTerm);
      setShowDSForm(false); setEditDS(null); setDsForm(emptyDS);
      setTimeout(() => setSuccess(''), 3000);
    } catch(e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  // Save bulk results
  const saveBulkResults = async () => {
    if (!bulkDS || !selTerm) return;
    setBulkSaving(true); setError('');
    try {
      const cls = classes.find(c => c.id === bulkDS.class_id);
      const rows = bulkResults
        .filter(r => r.marks_obtained !== '' || r.is_absent)
        .map(r => ({
          institution_id:         institution.id,
          institution_account_id: account.id,
          exam_term_id:           selTerm.id,
          datesheet_id:           bulkDS.id,
          class_id:               bulkDS.class_id,
          class_name:             cls?.name || bulkDS.class_name,
          student_id:             r.student_id,
          student_name:           r.student_name,
          student_username:       r.student_username,
          student_roll_number:    r.student_roll_number,
          subject:                bulkDS.subject,
          subject_code:           bulkDS.subject_code,
          teacher_id:             bulkDS.teacher_id,
          teacher_name:           bulkDS.teacher_name,
          total_marks:            Number(bulkDS.total_marks) || 100,
          marks_obtained:         r.is_absent ? null : (Number(r.marks_obtained) || 0),
          is_absent:              r.is_absent,
          teacher_remarks:        r.teacher_remarks,
        }));

      if (rows.length === 0) { setError('No results to save'); setBulkSaving(false); return; }

      // Upsert all results
      await supabase.from('lms_exam_results')
        .upsert(rows, { onConflict: 'exam_term_id,student_id,subject' });

      setSuccess(`Results saved for ${rows.length} students!`);
      setShowResultForm(false); setBulkResults([]); setBulkDS(null);
      await loadResults(selTerm);
      setTimeout(() => setSuccess(''), 4000);
    } catch(e) { setError(e.message || 'Save failed'); }
    finally { setBulkSaving(false); }
  };

  const deleteTerm = async (id) => {
    if (!window.confirm('Delete this exam term? All datesheets and results will be deleted.')) return;
    await supabase.from('lms_exam_terms').delete().eq('id', id);
    setTerms(prev => prev.filter(t => t.id !== id));
  };

  const deleteDS = async (id) => {
    if (!window.confirm('Delete this datesheet entry?')) return;
    await supabase.from('lms_exam_datesheets').delete().eq('id', id);
    setDatesheets(prev => prev.filter(d => d.id !== id));
  };

  const publishResults = async (term) => {
    await supabase.from('lms_exam_terms').update({
      results_published: true,
      results_published_at: new Date().toISOString(),
      status: 'result_announced',
    }).eq('id', term.id);
    setSuccess('Results published! Students can now see their results.');
    await loadInit();
    setTimeout(() => setSuccess(''), 4000);
  };

  // Countdown helper
  const getCountdown = (dateStr) => {
    if (!dateStr) return null;
    const diff = new Date(dateStr) - new Date();
    if (diff < 0) return null;
    const days  = Math.floor(diff / (1000*60*60*24));
    const hours = Math.floor((diff % (1000*60*60*24)) / (1000*60*60));
    if (days > 0) return `${days}d ${hours}h remaining`;
    return `${hours}h remaining`;
  };

  const filtered = terms.filter(t =>
    !search.trim() ||
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    (t.target_class_name||'').toLowerCase().includes(search.toLowerCase())
  );

  const inp = { width:'100%', padding:'9px 12px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13, boxSizing:'border-box', outline:'none', color:'#111827' };
  const F   = ({ label, children, col }) => (
    <div style={{ gridColumn: col||'span 1' }}>
      <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>{label}</label>
      {children}
    </div>
  );

  // ── GAZETTE VIEW ──────────────────────────────────────
  if (view === 'gazette') return (
    <LMSLayout>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <button onClick={() => setView('results')} style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer', fontSize:13, fontWeight:600, color:'#374151' }}>← Back</button>
        <div>
          <h1 style={{ fontSize:20, fontWeight:800, color:'#111827', margin:0 }}>Class Gazette</h1>
          <div style={{ fontSize:13, color:'#6B7280', marginTop:2 }}>{selTerm?.name} — {selClass?.name}</div>
        </div>
        <button onClick={() => window.print()} style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:10, border:'1px solid #E5E7EB', background:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', color:'#374151' }}>
          🖨️ Print Gazette
        </button>
      </div>
      {gazetteLoading ? (
        <div style={{ textAlign:'center', padding:48, color:'#9CA3AF' }}>Building gazette...</div>
      ) : !gazette || !gazette.students?.length ? (
        <div style={{ textAlign:'center', padding:48, background:'#fff', borderRadius:16, border:'1px solid #E5E7EB' }}>
          <Award size={40} color="#E5E7EB" style={{ marginBottom:12 }} />
          <div style={{ fontSize:15, fontWeight:700, color:'#374151' }}>No results found for this class</div>
          <div style={{ fontSize:13, color:'#9CA3AF', marginTop:4 }}>Enter results first to generate the gazette</div>
        </div>
      ) : (
        <div id="gazette-print">
          {/* Gazette header */}
          <div style={{ background:'linear-gradient(135deg,#1E293B,#0F172A)', borderRadius:16, padding:'24px 28px', marginBottom:20, textAlign:'center' }}>
            <div style={{ color:'rgba(255,255,255,0.6)', fontSize:12, marginBottom:4 }}>OFFICIAL GAZETTE</div>
            <div style={{ color:'#fff', fontSize:22, fontWeight:900, marginBottom:4 }}>{institution?.name}</div>
            <div style={{ color:'rgba(255,255,255,0.7)', fontSize:14 }}>{selTerm?.name} — Class {selClass?.name}</div>
            <div style={{ color:'rgba(255,255,255,0.5)', fontSize:12, marginTop:4 }}>{selTerm?.academic_year} · {selTerm?.semester}</div>
          </div>

          {/* Summary stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:20 }}>
            {[
              { label:'Total Students', value: gazette.students.length,                                          color:'#4F8EF7' },
              { label:'Passed',         value: gazette.students.filter(s=>s.overall_status==='PASS').length,    color:'#10B981' },
              { label:'Failed',         value: gazette.students.filter(s=>s.overall_status==='FAIL').length,    color:'#EF4444' },
              { label:'Pass Rate',      value: `${Math.round(gazette.students.filter(s=>s.overall_status==='PASS').length/gazette.students.length*100)}%`, color:'#7C3AED' },
              { label:'Avg %',          value: `${(gazette.students.reduce((a,s)=>a+(s.overall_percentage||0),0)/gazette.students.length).toFixed(1)}%`, color:'#F59E0B' },
              { label:'Avg CGPA',       value: (gazette.students.reduce((a,s)=>a+(s.cgpa||0),0)/gazette.students.length).toFixed(2), color:'#EC4899' },
            ].map(s => (
              <div key={s.label} style={{ background:'#fff', borderRadius:12, border:'1px solid #E5E7EB', padding:'14px 16px', textAlign:'center' }}>
                <div style={{ fontSize:22, fontWeight:900, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:11, color:'#6B7280' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Gazette table */}
          <div style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', overflow:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:800 }}>
              <thead>
                <tr style={{ background:'#1E293B' }}>
                  <th style={{ padding:'12px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#94A3B8', letterSpacing:0.5 }}>STUDENT</th>
                  {(gazette.subjects || []).map(sub => (
                    <th key={sub} style={{ padding:'12px 10px', textAlign:'center', fontSize:10, fontWeight:700, color:'#94A3B8', letterSpacing:0.3, minWidth:80 }}>{sub}</th>
                  ))}
                  <th style={{ padding:'12px 14px', textAlign:'center', fontSize:11, fontWeight:700, color:'#94A3B8' }}>TOTAL</th>
                  <th style={{ padding:'12px 14px', textAlign:'center', fontSize:11, fontWeight:700, color:'#94A3B8' }}>%</th>
                  <th style={{ padding:'12px 14px', textAlign:'center', fontSize:11, fontWeight:700, color:'#94A3B8' }}>CGPA</th>
                  <th style={{ padding:'12px 14px', textAlign:'center', fontSize:11, fontWeight:700, color:'#94A3B8' }}>RESULT</th>
                </tr>
              </thead>
              <tbody>
                {gazette.students.map((s, si) => (
                  <tr key={si} style={{ borderBottom:'1px solid #F1F5F9', background: si%2===0 ? '#fff' : '#FAFAFA' }}>
                    <td style={{ padding:'12px 14px' }}>
                      <div style={{ fontWeight:700, color:'#111827', fontSize:13 }}>{s.student_name}</div>
                      {s.student_roll_number && <div style={{ fontSize:11, color:'#9CA3AF' }}>Roll# {s.student_roll_number}</div>}
                    </td>
                    {(gazette.subjects || []).map(sub => {
                      const sr = s.subjects?.[sub];
                      return (
                        <td key={sub} style={{ padding:'10px', textAlign:'center' }}>
                          {!sr ? <span style={{ color:'#D1D5DB', fontSize:11 }}>—</span> : sr.is_absent ? (
                            <span style={{ fontSize:10, fontWeight:700, color:'#94A3B8', background:'#F1F5F9', padding:'2px 6px', borderRadius:4 }}>AB</span>
                          ) : (
                            <div>
                              <div style={{ fontSize:13, fontWeight:800, color:'#111827' }}>{sr.marks_obtained}</div>
                              <div style={{ fontSize:9, color: GRADE_COLORS[sr.grade] || '#374151', fontWeight:700 }}>{sr.grade}</div>
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td style={{ padding:'12px 14px', textAlign:'center', fontWeight:700, color:'#111827', fontSize:13 }}>
                      {s.total_marks_obtained}/{s.grand_total}
                    </td>
                    <td style={{ padding:'12px 14px', textAlign:'center' }}>
                      <span style={{ fontWeight:800, color: (s.overall_percentage||0)>=60?'#10B981':'#EF4444', fontSize:13 }}>
                        {s.overall_percentage?.toFixed(1)}%
                      </span>
                    </td>
                    <td style={{ padding:'12px 14px', textAlign:'center' }}>
                      <span style={{ fontWeight:800, color:'#7C3AED', fontSize:13 }}>{s.cgpa?.toFixed(2)}</span>
                    </td>
                    <td style={{ padding:'12px 14px', textAlign:'center' }}>
                      <span style={{
                        fontSize:11, fontWeight:800, padding:'3px 10px', borderRadius:6,
                        background: s.overall_status==='PASS' ? '#F0FDF4' : '#FEF2F2',
                        color:      s.overall_status==='PASS' ? '#065F46'  : '#991B1B',
                        border: `1px solid ${s.overall_status==='PASS' ? '#BBF7D0' : '#FECACA'}`,
                      }}>
                        {s.overall_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Top performers */}
          <div style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', padding:20, marginTop:16 }}>
            <div style={{ fontWeight:800, color:'#111827', fontSize:15, marginBottom:14 }}>🏆 Top Performers</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:12 }}>
              {[...gazette.students]
                .filter(s => s.overall_status === 'PASS')
                .sort((a,b) => (b.overall_percentage||0) - (a.overall_percentage||0))
                .slice(0, 3)
                .map((s, i) => (
                  <div key={i} style={{ padding:'14px 16px', borderRadius:12, background: i===0?'#FFF7ED':i===1?'#F8FAFF':'#F0FDF4', border:`1px solid ${i===0?'#FED7AA':i===1?'#BFDBFE':'#BBF7D0'}` }}>
                    <div style={{ fontSize:20, marginBottom:6 }}>{i===0?'🥇':i===1?'🥈':'🥉'}</div>
                    <div style={{ fontWeight:800, color:'#111827', fontSize:13 }}>{s.student_name}</div>
                    <div style={{ fontWeight:700, color: i===0?'#C2410C':i===1?'#1D4ED8':'#065F46', fontSize:15 }}>{s.overall_percentage?.toFixed(1)}%</div>
                    <div style={{ fontSize:11, color:'#6B7280' }}>CGPA: {s.cgpa?.toFixed(2)}</div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </LMSLayout>
  );

  // ── RESULTS VIEW ──────────────────────────────────────
  if (view === 'results') return (
    <LMSLayout>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <button onClick={() => setView('terms')} style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer', fontSize:13, fontWeight:600, color:'#374151' }}>← Back</button>
        <div style={{ flex:1 }}>
          <h1 style={{ fontSize:20, fontWeight:800, color:'#111827', margin:0 }}>Results — {selTerm?.name}</h1>
          <div style={{ fontSize:13, color:'#6B7280' }}>{results.length} result entries · Click on a datesheet to enter marks</div>
        </div>
        {!selTerm?.results_published && (
          <button onClick={() => publishResults(selTerm)}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 18px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#10B981,#059669)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
            📢 Publish Results
          </button>
        )}
        {selTerm?.results_published && (
          <span style={{ fontSize:12, fontWeight:700, color:'#065F46', background:'#F0FDF4', padding:'6px 14px', borderRadius:8, border:'1px solid #BBF7D0' }}>✅ Results Published</span>
        )}
      </div>
      {success && <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#065F46', fontWeight:600 }}>{success}</div>}
      {error   && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#991B1B' }}>{error}<button onClick={()=>setError('')} style={{ float:'right', background:'none', border:'none', cursor:'pointer' }}>✕</button></div>}

      {/* Datesheets for result entry */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontWeight:700, color:'#374151', fontSize:14, marginBottom:12 }}>Click on a subject to enter marks:</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:10 }}>
          {datesheets.map(ds => {
            const existingCount = results.filter(r => r.datesheet_id === ds.id).length;
            return (
              <button key={ds.id} onClick={() => loadStudentsForBulk(ds)}
                style={{ padding:'14px 16px', borderRadius:12, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer', textAlign:'left', display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:40, height:40, borderRadius:10, background:'#EFF6FF', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:18 }}>📝</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color:'#111827', fontSize:13 }}>{ds.subject}</div>
                  <div style={{ fontSize:11, color:'#6B7280' }}>{ds.class_name} · {ds.exam_date} · {ds.total_marks} marks</div>
                  {existingCount > 0 && <div style={{ fontSize:11, color:'#10B981', marginTop:2 }}>✓ {existingCount} results entered</div>}
                </div>
                <ChevronRight size={14} color="#9CA3AF" />
              </button>
            );
          })}
          {datesheets.length === 0 && (
            <div style={{ padding:24, textAlign:'center', color:'#9CA3AF', fontSize:13 }}>No datesheets found. Add datesheet entries first.</div>
          )}
        </div>
      </div>

      {/* Gazette generator */}
      {classes.length > 0 && (
        <div style={{ background:'#F5F3FF', border:'1px solid #DDD6FE', borderRadius:14, padding:16 }}>
          <div style={{ fontWeight:700, color:'#5B21B6', fontSize:14, marginBottom:10 }}>📊 Generate Class Gazette</div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {classes.map(cls => (
              <button key={cls.id} onClick={() => loadGazette(selTerm, cls)}
                style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #DDD6FE', background:'#fff', color:'#5B21B6', fontWeight:700, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                <Award size={13} /> {cls.name} Gazette
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bulk Result Entry Modal */}
      {showResultForm && bulkDS && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:720, maxHeight:'92vh', overflowY:'auto' }}>
            <div style={{ padding:'20px 24px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
              <div>
                <h3 style={{ margin:0, fontWeight:800, color:'#111827', fontSize:18 }}>Enter Marks — {bulkDS.subject}</h3>
                <div style={{ fontSize:12, color:'#6B7280', marginTop:2 }}>{bulkDS.class_name} · Total Marks: {bulkDS.total_marks} · Passing: {bulkDS.passing_marks}</div>
              </div>
              <button onClick={() => { setShowResultForm(false); setBulkResults([]); setBulkDS(null); }} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={22} color="#6B7280" /></button>
            </div>
            <div style={{ padding:20 }}>
              {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px', marginBottom:14, fontSize:13, color:'#991B1B' }}>{error}</div>}

              {/* Quick actions */}
              <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
                <button onClick={() => setBulkResults(prev => prev.map(r => ({...r, is_absent:false})))}
                  style={{ padding:'6px 12px', borderRadius:7, border:'1px solid #BBF7D0', background:'#F0FDF4', color:'#065F46', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  ✓ Mark All Present
                </button>
                <button onClick={() => setBulkResults(prev => prev.map(r => ({...r, is_absent:true, marks_obtained:''})))}
                  style={{ padding:'6px 12px', borderRadius:7, border:'1px solid #FECACA', background:'#FEF2F2', color:'#991B1B', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  Mark All Absent
                </button>
              </div>

              {/* Student marks table */}
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:'#F8FAFF' }}>
                    {['#','Student','Roll No','Marks','Absent','Remarks'].map(h => (
                      <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#6B7280', borderBottom:'1px solid #E5E7EB' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bulkResults.map((r, i) => (
                    <tr key={r.student_id} style={{ borderBottom:'1px solid #F3F4F6' }}>
                      <td style={{ padding:'10px 12px', fontSize:12, color:'#9CA3AF' }}>{i+1}</td>
                      <td style={{ padding:'10px 12px' }}>
                        <div style={{ fontWeight:700, color:'#111827', fontSize:13 }}>{r.student_name}</div>
                        <div style={{ fontSize:10, color:'#9CA3AF' }}>@{r.student_username}</div>
                      </td>
                      <td style={{ padding:'8px 10px' }}>
                        <input value={r.student_roll_number} onChange={e => setBulkResults(prev => prev.map((x,j) => j===i ? {...x,student_roll_number:e.target.value}:x))}
                          placeholder="Roll#" style={{ width:70, padding:'6px 8px', borderRadius:6, border:'1px solid #E5E7EB', fontSize:12, outline:'none' }} />
                      </td>
                      <td style={{ padding:'8px 10px' }}>
                        <input type="number" min="0" max={bulkDS.total_marks}
                          value={r.marks_obtained} disabled={r.is_absent}
                          onChange={e => setBulkResults(prev => prev.map((x,j) => j===i ? {...x,marks_obtained:e.target.value}:x))}
                          style={{ width:80, padding:'6px 8px', borderRadius:6, border:`1px solid ${!r.is_absent && r.marks_obtained!=='' && Number(r.marks_obtained)<bulkDS.passing_marks?'#FECACA':'#E5E7EB'}`, fontSize:13, fontWeight:700, outline:'none', background:r.is_absent?'#F9FAFB':'#fff', color: !r.is_absent && r.marks_obtained!=='' && Number(r.marks_obtained)<bulkDS.passing_marks?'#EF4444':'#111827' }} />
                        {!r.is_absent && r.marks_obtained !== '' && (
                          <span style={{ fontSize:10, color: Number(r.marks_obtained) >= bulkDS.passing_marks ? '#10B981' : '#EF4444', marginLeft:4, fontWeight:700 }}>
                            {Math.round(Number(r.marks_obtained)/bulkDS.total_marks*100)}%
                          </span>
                        )}
                      </td>
                      <td style={{ padding:'8px 10px', textAlign:'center' }}>
                        <input type="checkbox" checked={r.is_absent}
                          onChange={e => setBulkResults(prev => prev.map((x,j) => j===i ? {...x,is_absent:e.target.checked,marks_obtained:''}:x))}
                          style={{ width:16, height:16, cursor:'pointer' }} />
                      </td>
                      <td style={{ padding:'8px 10px' }}>
                        <input value={r.teacher_remarks} onChange={e => setBulkResults(prev => prev.map((x,j) => j===i ? {...x,teacher_remarks:e.target.value}:x))}
                          placeholder="Optional remark..." style={{ width:160, padding:'6px 8px', borderRadius:6, border:'1px solid #E5E7EB', fontSize:11, outline:'none' }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ display:'flex', gap:10, marginTop:16, position:'sticky', bottom:0, background:'#fff', paddingTop:12 }}>
                <button onClick={() => { setShowResultForm(false); setBulkResults([]); setBulkDS(null); }}
                  style={{ flex:1, padding:'12px 0', borderRadius:10, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, cursor:'pointer' }}>Cancel</button>
                <button onClick={saveBulkResults} disabled={bulkSaving}
                  style={{ flex:2, padding:'12px 0', borderRadius:10, border:'none', background:'linear-gradient(135deg,#10B981,#059669)', color:'#fff', fontWeight:700, cursor:bulkSaving?'wait':'pointer', opacity:bulkSaving?0.7:1 }}>
                  {bulkSaving ? 'Saving...' : `Save Results (${bulkResults.filter(r=>r.marks_obtained!==''||r.is_absent).length} students)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LMSLayout>
  );

  // ── DATESHEET VIEW ────────────────────────────────────
  if (view === 'datesheet') return (
    <LMSLayout>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <button onClick={() => setView('terms')} style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer', fontSize:13, fontWeight:600, color:'#374151' }}>← Back</button>
        <div style={{ flex:1 }}>
          <h1 style={{ fontSize:20, fontWeight:800, color:'#111827', margin:0 }}>Datesheet — {selTerm?.name}</h1>
          <div style={{ fontSize:13, color:'#6B7280' }}>{selTerm?.start_date} to {selTerm?.end_date}</div>
        </div>
        <button onClick={() => loadResults(selTerm)}
          style={{ padding:'9px 16px', borderRadius:10, border:'1px solid #E5E7EB', background:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', color:'#374151', display:'flex', alignItems:'center', gap:6 }}>
          <BarChart3 size={14} /> Results
        </button>
        <button onClick={() => { setShowDSForm(true); setEditDS(null); setDsForm(emptyDS); setError(''); }}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 18px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
          <Plus size={14} /> Add Exam
        </button>
      </div>
      {success && <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#065F46', fontWeight:600 }}>{success}</div>}
      {error   && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#991B1B' }}>{error}</div>}

      {/* Group by date */}
      {datesheets.length === 0 ? (
        <div style={{ textAlign:'center', padding:60, background:'#fff', borderRadius:16, border:'1px solid #E5E7EB' }}>
          <Calendar size={40} color="#E5E7EB" style={{ marginBottom:12 }} />
          <div style={{ fontSize:15, fontWeight:700, color:'#374151', marginBottom:6 }}>No exams added yet</div>
          <button onClick={() => { setShowDSForm(true); setDsForm(emptyDS); }}
            style={{ padding:'10px 24px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontWeight:700, cursor:'pointer', marginTop:12 }}>
            + Add First Exam
          </button>
        </div>
      ) : (
        Object.entries(
          datesheets.reduce((acc, ds) => {
            const key = ds.exam_date;
            if (!acc[key]) acc[key] = [];
            acc[key].push(ds);
            return acc;
          }, {})
        ).sort(([a],[b]) => a.localeCompare(b)).map(([date, dayExams]) => {
          const d = new Date(date + 'T00:00:00');
          const countdown = getCountdown(date);
          return (
            <div key={date} style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', marginBottom:14, overflow:'hidden' }}>
              <div style={{ padding:'14px 20px', background:'linear-gradient(135deg,#4F8EF710,#7C3AED08)', borderBottom:'1px solid #F3F4F6', display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:52, height:52, borderRadius:12, background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'#fff', flexShrink:0 }}>
                  <div style={{ fontSize:16, fontWeight:900, lineHeight:1 }}>{d.getDate()}</div>
                  <div style={{ fontSize:9, fontWeight:600 }}>{d.toLocaleDateString('en-US',{month:'short'})}</div>
                </div>
                <div>
                  <div style={{ fontWeight:800, color:'#111827', fontSize:15 }}>{d.toLocaleDateString('en-US',{weekday:'long', year:'numeric', month:'long', day:'numeric'})}</div>
                  <div style={{ fontSize:12, color:'#6B7280' }}>{dayExams.length} exam{dayExams.length!==1?'s':''}</div>
                </div>
                {countdown && <span style={{ marginLeft:'auto', fontSize:12, fontWeight:700, color:'#F59E0B', background:'#FFFBEB', padding:'4px 10px', borderRadius:6, border:'1px solid #FDE68A' }}>⏰ {countdown}</span>}
              </div>
              <div>
                {dayExams.map((ds, i) => (
                  <div key={ds.id} style={{ padding:'14px 20px', borderBottom: i<dayExams.length-1?'1px solid #F9FAFB':'none', display:'flex', alignItems:'center', gap:14 }}>
                    <div style={{ minWidth:80, textAlign:'center', padding:'8px 10px', borderRadius:10, background:'#F1F5F9' }}>
                      <div style={{ fontSize:12, fontWeight:800, color:'#374151' }}>{ds.start_time}</div>
                      <div style={{ fontSize:10, color:'#9CA3AF' }}>to</div>
                      <div style={{ fontSize:12, fontWeight:700, color:'#374151' }}>{ds.end_time}</div>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                        <span style={{ fontWeight:800, color:'#111827', fontSize:15 }}>{ds.subject}</span>
                        {ds.subject_code && <span style={{ fontSize:11, color:'#9CA3AF' }}>{ds.subject_code}</span>}
                        <span style={{ fontSize:10, fontWeight:700, color:'#4F8EF7', background:'#EFF6FF', padding:'2px 7px', borderRadius:4 }}>{ds.exam_type}</span>
                        {ds.is_cancelled && <span style={{ fontSize:10, fontWeight:700, color:'#991B1B', background:'#FEF2F2', padding:'2px 7px', borderRadius:4 }}>CANCELLED</span>}
                      </div>
                      <div style={{ display:'flex', gap:14, fontSize:12, color:'#6B7280', flexWrap:'wrap' }}>
                        <span>📚 {ds.class_name}</span>
                        {ds.teacher_name && <span>👤 {ds.teacher_name}</span>}
                        {ds.room && <span>📍 {ds.room}{ds.building?`, ${ds.building}`:''}</span>}
                        <span>📊 {ds.total_marks} marks (Pass: {ds.passing_marks})</span>
                        {ds.duration_minutes && <span>⏱ {ds.duration_minutes}min</span>}
                      </div>
                      {ds.syllabus_covered && <div style={{ fontSize:11, color:'#64748B', marginTop:4, background:'#F8FAFF', padding:'4px 8px', borderRadius:4 }}>📖 Syllabus: {ds.syllabus_covered}</div>}
                      {ds.instructions && <div style={{ fontSize:11, color:'#64748B', marginTop:4 }}>ℹ️ {ds.instructions}</div>}
                    </div>
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={() => { setEditDS(ds); setDsForm({...emptyDS,...ds}); setShowDSForm(true); setError(''); }}
                        style={{ padding:'7px 10px', borderRadius:7, border:'1px solid #E5E7EB', background:'#F9FAFB', cursor:'pointer' }}>
                        <Edit size={13} color="#374151" />
                      </button>
                      <button onClick={() => deleteDS(ds.id)}
                        style={{ padding:'7px 10px', borderRadius:7, border:'1px solid #FECACA', background:'#FEF2F2', cursor:'pointer' }}>
                        <Trash2 size={13} color="#EF4444" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}

      {/* Add/Edit Datesheet Modal */}
      {showDSForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:600, maxHeight:'92vh', overflowY:'auto' }}>
            <div style={{ padding:'20px 24px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
              <h3 style={{ margin:0, fontWeight:800, color:'#111827', fontSize:18 }}>{editDS ? 'Edit Exam Slot' : 'Add Exam to Datesheet'}</h3>
              <button onClick={() => { setShowDSForm(false); setEditDS(null); setDsForm(emptyDS); }} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={22} color="#6B7280" /></button>
            </div>
            <div style={{ padding:24 }}>
              {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px', marginBottom:14, fontSize:13, color:'#991B1B' }}>{error}</div>}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <F label="Class *">
                  <select value={dsForm.class_id} onChange={e=>setDsForm({...dsForm,class_id:e.target.value})} style={inp}>
                    <option value="">Select Class</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </F>
                <F label="Subject *">
                  <input value={dsForm.subject} onChange={e=>setDsForm({...dsForm,subject:e.target.value})} placeholder="e.g. Mathematics" style={inp} />
                </F>
                <F label="Subject Code">
                  <input value={dsForm.subject_code} onChange={e=>setDsForm({...dsForm,subject_code:e.target.value})} placeholder="e.g. MATH-301" style={inp} />
                </F>
                <F label="Teacher">
                  <select value={dsForm.teacher_id} onChange={e=>setDsForm({...dsForm,teacher_id:e.target.value})} style={inp}>
                    <option value="">No Teacher</option>
                    {teachers.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                  </select>
                </F>
                <F label="Exam Date *">
                  <input type="date" value={dsForm.exam_date} onChange={e=>setDsForm({...dsForm,exam_date:e.target.value})} style={inp} />
                </F>
                <F label="Exam Type">
                  <select value={dsForm.exam_type} onChange={e=>setDsForm({...dsForm,exam_type:e.target.value})} style={inp}>
                    {['written','practical','oral','mcq','online','project','other'].map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
                  </select>
                </F>
                <F label="Start Time *">
                  <input type="time" value={dsForm.start_time} onChange={e=>setDsForm({...dsForm,start_time:e.target.value})} style={inp} />
                </F>
                <F label="End Time *">
                  <input type="time" value={dsForm.end_time} onChange={e=>setDsForm({...dsForm,end_time:e.target.value})} style={inp} />
                </F>
                <F label="Total Marks">
                  <input type="number" value={dsForm.total_marks} onChange={e=>setDsForm({...dsForm,total_marks:e.target.value})} style={inp} />
                </F>
                <F label="Passing Marks">
                  <input type="number" value={dsForm.passing_marks} onChange={e=>setDsForm({...dsForm,passing_marks:e.target.value})} style={inp} />
                </F>
                <F label="Room">
                  <input value={dsForm.room} onChange={e=>setDsForm({...dsForm,room:e.target.value})} placeholder="e.g. Hall A" style={inp} />
                </F>
                <F label="Building">
                  <input value={dsForm.building} onChange={e=>setDsForm({...dsForm,building:e.target.value})} placeholder="e.g. Main Block" style={inp} />
                </F>
                <F label="Syllabus / Topics Covered" col="span 2">
                  <textarea value={dsForm.syllabus_covered} onChange={e=>setDsForm({...dsForm,syllabus_covered:e.target.value})} rows={2} placeholder="e.g. Chapter 1-5, Algebra, Geometry..." style={{ ...inp, resize:'vertical' }} />
                </F>
                <F label="Special Instructions" col="span 2">
                  <textarea value={dsForm.instructions} onChange={e=>setDsForm({...dsForm,instructions:e.target.value})} rows={2} placeholder="e.g. Bring calculator, No mobile phones allowed..." style={{ ...inp, resize:'vertical' }} />
                </F>
              </div>
              <div style={{ display:'flex', gap:10, marginTop:20 }}>
                <button onClick={() => { setShowDSForm(false); setEditDS(null); setDsForm(emptyDS); }}
                  style={{ flex:1, padding:'12px 0', borderRadius:10, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, cursor:'pointer' }}>Cancel</button>
                <button onClick={saveDS} disabled={saving}
                  style={{ flex:2, padding:'12px 0', borderRadius:10, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontWeight:700, cursor:saving?'wait':'pointer', opacity:saving?0.7:1 }}>
                  {saving ? 'Saving...' : editDS ? 'Update' : 'Add to Datesheet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LMSLayout>
  );

  // ── TERMS LIST VIEW ───────────────────────────────────
  return (
    <LMSLayout>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:'#111827', margin:0 }}>Exams & Results</h1>
          <p style={{ color:'#6B7280', fontSize:13, margin:'4px 0 0' }}>Create exam terms, build datesheets, enter results, generate gazettes</p>
        </div>
        <button onClick={() => { setShowTermForm(true); setEditTerm(null); setTermForm(emptyTerm); setError(''); }}
          style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 18px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
          <Plus size={16} /> New Exam Term
        </button>
      </div>
      {success && <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#065F46', fontWeight:600 }}>{success}</div>}
      {error   && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#991B1B' }}>{error}</div>}

      {/* Search */}
      <div style={{ background:'#fff', borderRadius:14, border:'1px solid #E5E7EB', padding:14, marginBottom:20 }}>
        <div style={{ position:'relative' }}>
          <Search size={15} color="#9CA3AF" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }} />
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search exam terms..."
            style={{ width:'100%', padding:'9px 9px 9px 32px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13, outline:'none', boxSizing:'border-box' }} />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:48, color:'#9CA3AF' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:60, background:'#fff', borderRadius:16, border:'1px solid #E5E7EB' }}>
          <BookOpen size={48} color="#E5E7EB" style={{ marginBottom:12 }} />
          <div style={{ fontSize:16, fontWeight:700, color:'#374151', marginBottom:6 }}>No exam terms yet</div>
          <div style={{ fontSize:13, color:'#9CA3AF', marginBottom:20 }}>Create an exam term to get started (e.g. Mid-Term 2025, Pre-Board 2025)</div>
          <button onClick={() => { setShowTermForm(true); setTermForm(emptyTerm); }}
            style={{ padding:'10px 24px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontWeight:700, cursor:'pointer' }}>
            + Create Exam Term
          </button>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {filtered.map(term => {
            const ss   = STATUS_STYLES[term.status] || STATUS_STYLES.upcoming;
            const now  = new Date();
            const start= new Date(term.start_date);
            const end  = new Date(term.end_date);
            const daysToStart = Math.ceil((start - now) / (1000*60*60*24));
            const daysLeft    = Math.ceil((end   - now) / (1000*60*60*24));
            const examCountdown = daysToStart > 0 ? `Starts in ${daysToStart} day${daysToStart!==1?'s':''}` : daysLeft > 0 ? `${daysLeft} day${daysLeft!==1?'s':''} remaining` : null;
            const resultCountdown = term.result_announcement_date && !term.results_published ?
              getCountdown(term.result_announcement_date) : null;

            return (
              <div key={term.id} style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', overflow:'hidden' }}>
                <div style={{ padding:'18px 22px', display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8, flexWrap:'wrap' }}>
                      <h3 style={{ margin:0, fontWeight:900, color:'#111827', fontSize:17 }}>{term.name}</h3>
                      <span style={{ fontSize:11, fontWeight:700, color:ss.color, background:ss.bg, border:`1px solid ${ss.border}`, padding:'3px 10px', borderRadius:6 }}>{ss.label}</span>
                      {term.results_published && <span style={{ fontSize:11, fontWeight:700, color:'#5B21B6', background:'#F5F3FF', padding:'3px 10px', borderRadius:6, border:'1px solid #DDD6FE' }}>📊 Results Published</span>}
                      <span style={{ fontSize:11, fontWeight:600, color:'#9CA3AF', background:'#F3F4F6', padding:'3px 10px', borderRadius:5 }}>
                        {TERM_TYPES.find(t=>t.value===term.term_type)?.label || term.term_type}
                      </span>
                    </div>
                    <div style={{ display:'flex', gap:16, fontSize:13, color:'#6B7280', flexWrap:'wrap' }}>
                      <span>📅 {term.start_date} → {term.end_date}</span>
                      {term.target_class_name && <span>📚 {term.target_class_name}</span>}
                      {term.academic_year && <span>🎓 {term.academic_year}</span>}
                      {term.semester && <span>📖 {term.semester}</span>}
                    </div>
                    {examCountdown && (
                      <div style={{ marginTop:8, display:'inline-flex', alignItems:'center', gap:6, fontSize:12, fontWeight:700, color:'#F59E0B', background:'#FFFBEB', padding:'4px 10px', borderRadius:6, border:'1px solid #FDE68A' }}>
                        ⏰ {examCountdown}
                      </div>
                    )}
                    {resultCountdown && (
                      <div style={{ marginTop:8, marginLeft:8, display:'inline-flex', alignItems:'center', gap:6, fontSize:12, fontWeight:700, color:'#7C3AED', background:'#F5F3FF', padding:'4px 10px', borderRadius:6, border:'1px solid #DDD6FE' }}>
                        📊 Results in {resultCountdown}
                      </div>
                    )}
                    {term.result_announcement_date && term.results_published && (
                      <div style={{ marginTop:8, display:'inline-flex', alignItems:'center', gap:6, fontSize:12, fontWeight:700, color:'#065F46', background:'#F0FDF4', padding:'4px 10px', borderRadius:6, border:'1px solid #BBF7D0' }}>
                        ✅ Results announced on {term.results_published_at ? new Date(term.results_published_at).toLocaleDateString() : term.result_announcement_date}
                      </div>
                    )}
                  </div>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    <button onClick={() => loadDatesheets(term)}
                      style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #BFDBFE', background:'#EFF6FF', cursor:'pointer', fontSize:12, fontWeight:700, color:'#1D4ED8', display:'flex', alignItems:'center', gap:5 }}>
                      <Calendar size={13} /> Datesheet
                    </button>
                    <button onClick={() => { loadDatesheets(term); setTimeout(() => loadResults(term), 100); }}
                      style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #BBF7D0', background:'#F0FDF4', cursor:'pointer', fontSize:12, fontWeight:700, color:'#065F46', display:'flex', alignItems:'center', gap:5 }}>
                      <BarChart3 size={13} /> Results
                    </button>
                    <button onClick={() => { setEditTerm(term); setTermForm({...emptyTerm,...term,target_year:term.target_year||'',target_class_id:term.target_class_id||''}); setShowTermForm(true); setError(''); }}
                      style={{ padding:'8px 10px', borderRadius:8, border:'1px solid #E5E7EB', background:'#F9FAFB', cursor:'pointer' }}>
                      <Edit size={13} color="#374151" />
                    </button>
                    <button onClick={() => deleteTerm(term.id)}
                      style={{ padding:'8px 10px', borderRadius:8, border:'1px solid #FECACA', background:'#FEF2F2', cursor:'pointer' }}>
                      <Trash2 size={13} color="#EF4444" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Term Modal */}
      {showTermForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:620, maxHeight:'92vh', overflowY:'auto' }}>
            <div style={{ padding:'20px 24px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
              <h3 style={{ margin:0, fontWeight:800, color:'#111827', fontSize:18 }}>{editTerm ? 'Edit Exam Term' : 'Create New Exam Term'}</h3>
              <button onClick={() => { setShowTermForm(false); setEditTerm(null); setTermForm(emptyTerm); }} style={{ background:'none', border:'none', cursor:'pointer' }}><X size={22} color="#6B7280" /></button>
            </div>
            <div style={{ padding:24 }}>
              {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px', marginBottom:14, fontSize:13, color:'#991B1B' }}>{error}</div>}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <F label="Exam Term Name *" col="span 2">
                  <input value={termForm.name} onChange={e=>setTermForm({...termForm,name:e.target.value})} placeholder="e.g. Pre-Board 2025, Mid-Term Fall 2024" style={inp} />
                </F>
                <F label="Term Type *">
                  <select value={termForm.term_type} onChange={e=>setTermForm({...termForm,term_type:e.target.value})} style={inp}>
                    {TERM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </F>
                <F label="Status">
                  <select value={termForm.status} onChange={e=>setTermForm({...termForm,status:e.target.value})} style={inp}>
                    {Object.entries(STATUS_STYLES).map(([v,s]) => <option key={v} value={v}>{s.label}</option>)}
                  </select>
                </F>
                <F label="Start Date *">
                  <input type="date" value={termForm.start_date} onChange={e=>setTermForm({...termForm,start_date:e.target.value})} style={inp} />
                </F>
                <F label="End Date *">
                  <input type="date" value={termForm.end_date} onChange={e=>setTermForm({...termForm,end_date:e.target.value})} style={inp} />
                </F>
                <F label="Result Announcement Date">
                  <input type="date" value={termForm.result_announcement_date} onChange={e=>setTermForm({...termForm,result_announcement_date:e.target.value})} style={inp} />
                </F>
                <F label="Academic Year">
                  <input value={termForm.academic_year} onChange={e=>setTermForm({...termForm,academic_year:e.target.value})} placeholder="e.g. 2024-25" style={inp} />
                </F>
                <F label="Semester">
                  <input value={termForm.semester} onChange={e=>setTermForm({...termForm,semester:e.target.value})} placeholder="e.g. Fall 2024" style={inp} />
                </F>
                <F label="Target Class">
                  <select value={termForm.target_class_id} onChange={e=>setTermForm({...termForm,target_class_id:e.target.value})} style={inp}>
                    <option value="">All Classes</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </F>
                <F label="Target Degree">
                  <select value={termForm.target_degree} onChange={e=>setTermForm({...termForm,target_degree:e.target.value})} style={inp}>
                    <option value="all">All Students</option>
                    {['BS','BE','BBA','MS','MBA','PhD','FSc','ICS'].map(d=><option key={d} value={d}>{d}</option>)}
                  </select>
                </F>
                <F label="Instructions / Notes" col="span 2">
                  <textarea value={termForm.instructions} onChange={e=>setTermForm({...termForm,instructions:e.target.value})} rows={2} placeholder="General exam instructions..." style={{ ...inp, resize:'vertical' }} />
                </F>
                <div style={{ gridColumn:'span 2' }}>
                  <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, fontWeight:600, color:'#374151' }}>
                    <input type="checkbox" checked={termForm.is_published} onChange={e=>setTermForm({...termForm,is_published:e.target.checked})} style={{ width:16, height:16 }} />
                    Publish (visible to students on app)
                  </label>
                </div>
              </div>
              <div style={{ display:'flex', gap:10, marginTop:20 }}>
                <button onClick={() => { setShowTermForm(false); setEditTerm(null); setTermForm(emptyTerm); }}
                  style={{ flex:1, padding:'12px 0', borderRadius:10, border:'1.5px solid #E5E7EB', background:'#fff', color:'#374151', fontWeight:600, cursor:'pointer' }}>Cancel</button>
                <button onClick={saveTerm} disabled={saving}
                  style={{ flex:2, padding:'12px 0', borderRadius:10, border:'none', background:'linear-gradient(135deg,#4F8EF7,#7C3AED)', color:'#fff', fontWeight:700, cursor:saving?'wait':'pointer', opacity:saving?0.7:1 }}>
                  {saving ? 'Saving...' : editTerm ? 'Update Term' : 'Create Exam Term'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LMSLayout>
  );
}